import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Copy, Check, MoonStar } from "lucide-react";
import { toast } from "sonner";
import { PROMO_CODE } from "@/lib/data";

const ARM_DELAY_MS = 10000;
const SHOWN_KEY = "astra_exit_shown";

export function ExitIntent({ onClaim }) {
  const [open, setOpen] = useState(false);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    const alreadyPromo = localStorage.getItem("astra_promo") === PROMO_CODE;
    if (alreadyPromo || sessionStorage.getItem(SHOWN_KEY)) return;

    let armed = false;
    let maxScroll = 0;
    let lastY = window.scrollY;
    let lastT = Date.now();
    const armTimer = setTimeout(() => { armed = true; }, ARM_DELAY_MS);

    const trigger = () => {
      if (!armed || sessionStorage.getItem(SHOWN_KEY)) return;
      if (localStorage.getItem("astra_promo") === PROMO_CODE) return;
      sessionStorage.setItem(SHOWN_KEY, "1");
      setOpen(true);
    };

    // Desktop: pointer leaves through the top of the window
    const onMouseLeave = (e) => {
      if (e.clientY <= 8 && !e.relatedTarget) trigger();
    };

    // Mobile proxy: fast scroll back to the very top after reading deep
    const onScroll = () => {
      const y = window.scrollY;
      const now = Date.now();
      maxScroll = Math.max(maxScroll, y);
      const docH = document.documentElement.scrollHeight - window.innerHeight;
      const vel = (lastY - y) / Math.max(1, now - lastT); // px per ms, positive = scrolling up
      if (y < 100 && vel > 1.6 && maxScroll > docH * 0.5) trigger();
      lastY = y;
      lastT = now;
    };

    document.addEventListener("mouseleave", onMouseLeave);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => {
      clearTimeout(armTimer);
      document.removeEventListener("mouseleave", onMouseLeave);
      window.removeEventListener("scroll", onScroll);
    };
  }, []);

  const copyCode = async () => {
    try {
      await navigator.clipboard.writeText(PROMO_CODE);
      setCopied(true);
      toast.success("Code copied — it applies automatically at checkout");
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.info(`Your code: ${PROMO_CODE}`);
    }
  };

  const claim = () => {
    localStorage.setItem("astra_promo", PROMO_CODE);
    setOpen(false);
    onClaim();
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          data-testid="exit-intent-modal"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/75 p-5 backdrop-blur-sm"
          onClick={() => setOpen(false)}
        >
          <motion.div
            initial={{ scale: 0.92, y: 24, opacity: 0 }}
            animate={{ scale: 1, y: 0, opacity: 1 }}
            exit={{ scale: 0.95, y: 16, opacity: 0 }}
            transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
            onClick={(e) => e.stopPropagation()}
            className="relative w-full max-w-md rounded-3xl border border-white/15 bg-[#0e131f] p-8 text-center"
          >
            <button
              data-testid="exit-intent-close-button"
              onClick={() => setOpen(false)}
              className="absolute right-4 top-4 rounded-full border border-white/15 p-2 text-slate-400 transition-colors hover:text-white"
              aria-label="Close"
            >
              <X className="h-4 w-4" />
            </button>
            <span className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-cyan-400/15">
              <MoonStar className="h-6 w-6 text-cyan-300" />
            </span>
            <p className="mt-5 font-mono text-[10px] uppercase tracking-[0.25em] text-cyan-400">Before You Go</p>
            <h3 className="font-display mt-2 text-2xl font-extrabold text-white sm:text-3xl">
              Take 10% off your first good night.
            </h3>
            <p className="mt-3 text-sm leading-relaxed text-slate-400">
              One last thing — here's an extra 10% off the launch price. Tonight's chapter is a
              five-minute read; your well-rested future self says thanks.
            </p>
            <div className="mt-6 flex items-center justify-center gap-3">
              <span data-testid="exit-intent-code" className="rounded-xl border border-dashed border-cyan-400/50 bg-cyan-400/10 px-5 py-3 font-mono text-lg font-bold tracking-[0.2em] text-cyan-300">
                {PROMO_CODE}
              </span>
              <button
                data-testid="exit-intent-copy-code"
                onClick={copyCode}
                className="flex items-center gap-1.5 rounded-xl border border-white/15 px-4 py-3 text-xs font-semibold text-slate-300 transition-colors hover:border-cyan-400/50 hover:text-white"
              >
                {copied ? <Check className="h-3.5 w-3.5 text-emerald-400" /> : <Copy className="h-3.5 w-3.5" />}
                {copied ? "Copied" : "Copy"}
              </button>
            </div>
            <button
              data-testid="exit-intent-claim-button"
              onClick={claim}
              className="mt-5 w-full rounded-full bg-cyan-400 py-4 text-base font-bold text-[#07090e] shadow-[0_0_36px_rgba(56,189,248,0.3)] transition-transform hover:scale-[1.02] active:scale-95"
            >
              Claim 10% Off — Start Tonight
            </button>
            <p className="mt-3 text-[11px] text-slate-500">Stacks with the 70% launch discount · 30-night guarantee</p>
            <button
              data-testid="exit-intent-dismiss-link"
              onClick={() => setOpen(false)}
              className="mt-4 text-xs text-slate-600 underline-offset-2 transition-colors hover:text-slate-400 hover:underline"
            >
              No thanks, I sleep fine
            </button>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
